import {
  Home, Network, MessagesSquare, Lightbulb, Bot, Gauge, Library, Settings, Building2, Activity, Waypoints, Workflow,
  type LucideIcon,
} from 'lucide-react'
import i18n from 'i18next'
import { SHELL_ROLES, type Permission, type ShellRole } from './permissions'
import { showComingSoon } from './comingSoon'

// Variant C navigation: a module bar plus a panel that changes with the module
// (Artur's product structure). Each module follows the Learn → Redesign → Build →
// Measure loop; screens that are not built yet route to /soon/:key. Who sees an
// item is decided in permissions.ts.

export interface PanelItem {
  key: string // label under admin:shellNav.items.<key>
  icon?: LucideIcon
  // Absolute path; client-zone paths get ?org_id= appended by the Sidebar.
  // Omitted for screens that do not exist yet: they go to /soon/<key>.
  to?: string
  // The screen exists but its feature has not landed, so it counts as coming soon.
  soon?: boolean
  query?: string
  orgInPath?: boolean
  platformAdminOnly?: boolean
  permission?: Permission
  // A screen planned for a later Process Intelligence phase.
  phase?: 2 | 3
  // Product module the screen needs; when it is off, the consultant sees the item marked and client roles do not.
  requiresModule?: string
}

export interface PanelGroup {
  key: string // heading under admin:shellNav.groups.<key>
  items: PanelItem[]
}

// A module shows when at least one of its items is visible to the role.
export interface ShellModule {
  key: string // label under admin:shellNav.modules.<key>
  icon: LucideIcon
  zone: 'platform' | 'client'
  place?: 'main' | 'bottom'
  // Path prefixes that make this module active (first one is the landing page).
  paths: string[]
  groups: PanelGroup[]
  // The panel also shows favourites and the area tree for this module.
  areaTree?: boolean
  platformAdminOnly?: boolean
  badgeKey?: string
}

export const SHELL_MODULES: ShellModule[] = [
  {
    key: 'home', icon: Home, zone: 'client', paths: ['/chat', '/soon/coverage', '/overview'],
    groups: [{ key: 'clara', items: [
      { key: 'askClara', to: '/chat', permission: 'clara.chat' },
      { key: 'coverage', permission: 'process.landscape', phase: 2 },
      { key: 'overview', to: '/overview', permission: 'org.overview' },
    ] }],
  },
  {
    key: 'organization', icon: Network, zone: 'client', paths: ['/processes', '/soon/myTasks', '/soon/waitingOnClient', '/soon/noRecommendation', '/soon/owners', '/soon/systems', '/soon/handoffs'], areaTree: true,
    groups: [
      { key: 'quickViews', items: [
        { key: 'allProcesses', to: '/processes', permission: 'process.view_approved', requiresModule: 'process_intelligence' },
        { key: 'myTasks', permission: 'process.view_approved' },
        { key: 'waitingOnClient', permission: 'process.manage' },
        { key: 'inProgress', to: '/processes', query: 'status=in_progress', permission: 'process.manage', requiresModule: 'process_intelligence' },
        { key: 'new', to: '/processes', query: 'status=new', permission: 'process.manage', requiresModule: 'process_intelligence' },
        { key: 'noRecommendation', permission: 'process.view_approved' },
      ] },
      { key: 'twin', items: [
        { key: 'owners', permission: 'process.landscape', phase: 2 },
        { key: 'systems', permission: 'process.landscape', phase: 2 },
        { key: 'handoffs', permission: 'process.landscape', phase: 2 },
      ] },
    ],
  },
  {
    key: 'interviews', icon: MessagesSquare, zone: 'client', paths: ['/interviews', '/assessments', '/playbooks', '/soon/invitations', '/soon/myInterviews'], badgeKey: 'assessments',
    groups: [
      { key: 'interviews', items: [
        { key: 'interviewLibrary', to: '/interviews', orgInPath: true, permission: 'interview.view_sources' },
        { key: 'myInterviews', permission: 'interview.own' },
        { key: 'invitations', permission: 'interview.configure' },
      ] },
      { key: 'assessment', items: [
        { key: 'assessments', to: '/assessments', permission: 'interview.configure' },
        { key: 'playbooks', to: '/playbooks', permission: 'interview.configure' },
      ] },
    ],
  },
  {
    key: 'opportunities', icon: Lightbulb, zone: 'client', paths: ['/initiatives', '/soon/matrix', '/soon/toBe', '/soon/prototypes'],
    groups: [{ key: 'redesign', items: [
      { key: 'initiatives', to: '/initiatives', soon: true, permission: 'initiative.view', phase: 3 },
      { key: 'matrix', permission: 'initiative.view', phase: 3 },
      { key: 'toBe', permission: 'tobe.view', phase: 3 },
      { key: 'prototypes', permission: 'tobe.view', phase: 3 },
    ] }],
  },
  {
    key: 'agents', icon: Bot, zone: 'client', paths: ['/soon/agentPrototypes', '/soon/productionAgents'],
    groups: [{ key: 'build', items: [
      { key: 'agentPrototypes', permission: 'agent.view', phase: 3 },
      { key: 'productionAgents', permission: 'agent.view', phase: 3 },
    ] }],
  },
  {
    key: 'impact', icon: Gauge, zone: 'client', paths: ['/ai-usage', '/business-case'],
    groups: [{ key: 'measure', items: [
      { key: 'usageAndCost', to: '/ai-usage', permission: 'usage.view' },
      { key: 'businessCase', to: '/business-case', permission: 'initiative.view', phase: 3 },
    ] }],
  },
  {
    key: 'knowledge', icon: Library, zone: 'client', paths: ['/second-brain', '/tier2', '/evals', '/skills', '/organization-profile', '/ai-tools', '/soon/documents'],
    groups: [
      { key: 'learn', items: [
        { key: 'contextPacks', to: '/second-brain', permission: 'knowledge.view', requiresModule: 'second_brain' },
        { key: 'documents', permission: 'process.upload_documents' },
        { key: 'organizationProfile', to: '/organization-profile', permission: 'profile.view', requiresModule: 'second_brain' },
        { key: 'connectors', to: '/tier2', permission: 'knowledge.configure', requiresModule: 'second_brain' },
      ] },
      { key: 'quality', items: [
        { key: 'evals', to: '/evals', permission: 'knowledge.configure', requiresModule: 'second_brain' },
        { key: 'skills', to: '/skills', permission: 'knowledge.configure', requiresModule: 'second_brain' },
        { key: 'aiTools', to: '/ai-tools', permission: 'ai_tools.use', requiresModule: 'mcp' },
      ] },
    ],
  },
  {
    key: 'settings', icon: Settings, zone: 'client', place: 'bottom', paths: ['/clients/', '/meetings'],
    groups: [{ key: 'settings', items: [
      { key: 'organization', to: '/clients', orgInPath: true, permission: 'settings.manage' },
      { key: 'team', to: '/clients', orgInPath: true, query: 'tab=users', permission: 'settings.manage' },
      { key: 'meetings', to: '/meetings', platformAdminOnly: true, permission: 'settings.manage' },
    ] }],
  },

  // Platform level: above the clients, before one is chosen.
  {
    key: 'allClients', icon: Building2, zone: 'platform', paths: ['/home', '/clients'],
    groups: [{ key: 'platform', items: [
      { key: 'platformHome', to: '/home' },
      { key: 'clientList', to: '/clients' },
    ] }],
  },
  {
    key: 'mcpUsage', icon: Activity, zone: 'platform', paths: ['/mcp-usage'],
    groups: [{ key: 'platform', items: [{ key: 'mcpUsage', to: '/mcp-usage' }] }],
  },
  {
    key: 'aiGateway', icon: Waypoints, zone: 'platform', paths: ['/ai-gateway'], platformAdminOnly: true,
    groups: [{ key: 'platform', items: [{ key: 'aiGateway', to: '/ai-gateway' }] }],
  },
  {
    key: 'blueprints', icon: Workflow, zone: 'platform', paths: ['/blueprints'], platformAdminOnly: true,
    groups: [{ key: 'platform', items: [{ key: 'blueprints', to: '/blueprints' }] }],
  },
]

export function moduleForPath(pathname: string, zone: 'platform' | 'client'): ShellModule | undefined {
  const inZone = SHELL_MODULES.filter((m) => m.zone === zone)
  // Longest matching prefix wins, so /clients/42 (settings) beats /clients (platform).
  let best: { m: ShellModule; len: number } | undefined
  for (const m of inZone) {
    for (const p of m.paths) {
      const hit = pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`)
      if (hit && (!best || p.length > best.len)) best = { m, len: p.length }
    }
  }
  return best?.m
}

export const shellLabel = (kind: 'modules' | 'items' | 'groups' | 'viewAs', key: string): string =>
  i18n.t(`admin:shellNav.${kind}.${key}`)

export const isSoon = (item: PanelItem): boolean => !item.to || !!item.soon
export const soonVisible = (item: PanelItem): boolean => !isSoon(item) || showComingSoon()

// Later-phase screens show to whoever can act on them: all for the consultant, the
// roadmap modules for the sponsor, none for the employee.
const SPONSOR_ROADMAP_MODULES = ['opportunities', 'agents', 'impact']

export function phaseVisible(role: ShellRole, item: PanelItem, moduleKey: string): boolean {
  if (!item.phase || role === 'consultant') return true
  return role === 'sponsor' && SPONSOR_ROADMAP_MODULES.includes(moduleKey)
}

// "View as" lasts for one client in one tab; another client or a new session starts from the real role.
const VIEW_AS_KEY = 'kx-view-as'

export function storedViewAs(orgId: string | null): ShellRole | null {
  if (!orgId) return null
  try {
    const v = sessionStorage.getItem(`${VIEW_AS_KEY}:${orgId}`)
    return SHELL_ROLES.includes(v as ShellRole) ? (v as ShellRole) : null
  } catch {
    return null
  }
}

export function storeViewAs(orgId: string | null, role: ShellRole) {
  if (!orgId) return
  try {
    sessionStorage.setItem(`${VIEW_AS_KEY}:${orgId}`, role)
  } catch {
    /* best-effort */
  }
}
